import { useCallback } from 'react'
import { useStore } from 'react-admin'
import { convertGlucose, formatGlucoseValue, type GlucoseUnit } from '../utils/glucoseUnits'

/**
 * The ONE binding between the portal's glucose-unit preference (the app-bar
 * selector, store key 'glucoseUnit') and the display conversion helpers.
 *
 * Twelve components used to re-declare byte-identical `convertValue` /
 * `formatValue` closures over their own `useStore('glucoseUnit', ...)` read —
 * and one drifted to a hardcoded `/ 18.0182`. Read the trio from here instead;
 * components that resolve the unit some other way (e.g. a prop override for
 * PDF capture) should call `convertGlucose` / `formatGlucoseValue` from
 * utils/glucoseUnits with their resolved unit.
 *
 * NOTE: values here are DISPLAY conversions only — out-of-range masking is
 * `formatGlucoseForDisplay` (src/common.ts), and back-conversion for mask
 * tests is `displayGlucoseToMmol`. Don't reimplement either with this trio.
 */
export function useGlucoseDisplay() {
  const [glucoseUnit] = useStore<GlucoseUnit>('glucoseUnit', 'mmol/L')

  const convertValue = useCallback(
    (mmolValue: number) => convertGlucose(mmolValue, glucoseUnit),
    [glucoseUnit],
  )
  const formatValue = useCallback(
    (mmolValue: number) => formatGlucoseValue(mmolValue, glucoseUnit),
    [glucoseUnit],
  )

  return { glucoseUnit, convertValue, formatValue }
}
